import type { Offer } from '@libs/common-types';

/** Loyalty API `offerType` for dedicated in-store events. */
export const STORE_EVENT_OFFER_TYPE = 'Event';
export const FEATURED_STORE_EVENT_OFFER_TYPE = 'Featured Event';
export const HOME_STORE_EVENTS_LIMIT = 2;

export function isStoreEventOffer(offer: Offer): boolean {
    return offer.offerType === STORE_EVENT_OFFER_TYPE || offer.offerType === FEATURED_STORE_EVENT_OFFER_TYPE;
}

export function excludeStoreEventOffers(offers: Offer[] = []): Offer[] {
    return offers.filter(offer => !isStoreEventOffer(offer));
}

export function getHomeStoreEventOffers(offers?: Offer[] | null): Offer[] {
    return filterOffersByType({
        offers: offers ?? [],
        type: FEATURED_STORE_EVENT_OFFER_TYPE,
    }).slice(0, HOME_STORE_EVENTS_LIMIT);
}

export function formatOfferDate(dateStr: string): string {
    // Parse the string into a Date object
    const date = new Date(dateStr);

    // Extract month, day, and year
    const month = date.getMonth() + 1; // getMonth() returns months from 0-11
    const day = date.getDate();
    const year = date.getFullYear();

    // Return the formatted string
    return `${month}/${day}/${year}`;
}

export function sortByDisplayRank() {
    return (a: Offer, b: Offer): number => {
        if (a.displayRank === b.displayRank) {
            /**
             * If two offers have the same displayRank,
             * sort them alphabetically in the UI
             */

            if (a.shortDescription < b.shortDescription) {
                return -1;
            }

            if (a.shortDescription > b.shortDescription) {
                return 1;
            }

            return 0;
        }

        if (a.displayRank === null) {
            return 1;
        }

        if (b.displayRank === null) {
            return -1;
        }

        return a.displayRank - b.displayRank;
    };
}

function compareNullableRanks(aRank?: number | null, bRank?: number | null): number {
    if (aRank == null) {
        return bRank == null ? 0 : 1;
    }

    if (bRank == null) {
        return -1;
    }

    return aRank - bRank;
}

function compareShortDescriptions(a: Offer, b: Offer): number {
    return a.shortDescription.localeCompare(b.shortDescription, undefined, {
        numeric: true,
        sensitivity: 'base',
    });
}

export function sortByRecommendedDisplayRankOptimizely() {
    const compareCardSize = sortByCardSize();

    return (a: Offer, b: Offer): number => {
        const groupComparison = compareCardSize(a, b);

        if (groupComparison !== 0) {
            return groupComparison;
        }

        const aRecommendedRank = a.recommendedDisplayRank;
        const bRecommendedRank = b.recommendedDisplayRank;

        if (aRecommendedRank != null || bRecommendedRank != null) {
            return compareNullableRanks(aRecommendedRank, bRecommendedRank) || compareShortDescriptions(a, b);
        }

        return compareNullableRanks(a.displayRank, b.displayRank) || compareShortDescriptions(a, b);
    };
}

export function sortByCardSize() {
    return (a: Offer, b: Offer): number => {
        if (a.cardSize === b.cardSize) {
            return 0;
        }

        if (a.cardSize === '') {
            return 1;
        }

        if (b.cardSize === '') {
            return -1;
        }

        if (a.cardSize === 'Small') {
            return 1;
        }

        return -1;
    };
}

export function filterOffersByType({ offers, type }: { offers?: Offer[]; type?: string }): Offer[] {
    let offersMap = offers
        ?.map(offer => ({
            ...offer,
            expiration: {
                title: formatOfferDate(offer.availabilityEnd),
                description: 'See details',
            },
        }))
        .sort(sortByDisplayRank())
        .sort(sortByCardSize());

    if (type) {
        offersMap = offersMap?.filter(offer => offer.offerType === type);
        if (type === 'Featured Offer' || type === FEATURED_STORE_EVENT_OFFER_TYPE) {
            return offersMap?.sort(sortByDisplayRank()) || [];
        }
    }

    return offersMap || [];
}

export function filterOffersByTypeOptimizely({ offers, type }: { offers?: Offer[]; type?: string }): Offer[] {
    return (
        offers
            ?.map(offer => ({
                ...offer,
                expiration: {
                    title: formatOfferDate(offer.availabilityEnd),
                    description: 'See details',
                },
            }))
            .filter(offer => !type || offer.offerType === type)
            .sort(sortByRecommendedDisplayRankOptimizely()) ?? []
    );
}
